/** This file must only contain pure code and pure imports */

import { Scene } from "../scene.pure";
import { SceneComponentConstants, type ISceneComponent } from "../sceneComponent";

import { type SmartArrayNoDuplicate } from "../Misc/smartArray";
import { type RenderTargetTexture } from "../Materials/Textures/renderTargetTexture.pure";
import { type Nullable } from "../types";
import { Constants } from "../Engines/constants";
import { type GeometryBufferRenderer, type IGeometryBufferTextureTypeAndFormat } from "./geometryBufferRenderer.pure";

/**
 * Defines the Geometry Buffer scene component responsible to manage a G-Buffer useful
 * in several rendering techniques.
 */
export class GeometryBufferRendererSceneComponent implements ISceneComponent {
    /**
     * The component name helpful to identify the component in the list of scene components.
     */
    public readonly name = SceneComponentConstants.NAME_GEOMETRYBUFFERRENDERER;

    /**
     * The scene the component belongs to.
     */
    public scene: Scene;

    /**
     * Creates a new instance of the component for the given scene
     * @param scene Defines the scene to register the component in
     */
    constructor(scene: Scene) {
        this.scene = scene;
    }

    /**
     * Registers the component in a given scene
     */
    public register(): void {
        this.scene._gatherRenderTargetsStage.registerStep(SceneComponentConstants.STEP_GATHERRENDERTARGETS_GEOMETRYBUFFERRENDERER, this, this._gatherRenderTargets);
    }

    /**
     * Rebuilds the elements related to this component in case of
     * context lost for instance.
     */
    public rebuild(): void {
        // Nothing to do for this component
    }

    /**
     * Disposes the component and the associated resources
     */
    public dispose(): void {
        // Nothing to do for this component
    }

    private _gatherRenderTargets(renderTargets: SmartArrayNoDuplicate<RenderTargetTexture>): void {
        if (this.scene._geometryBufferRenderer) {
            renderTargets.push(this.scene._geometryBufferRenderer.getGBuffer());
        }
    }
}

let _Registered = false;
/**
 * Register side effects for geometryBufferRendererSceneComponent.
 * Safe to call multiple times; only the first call has an effect.
 * @param geometryBufferRendererClass The GeometryBufferRenderer class to register the component for
 */
export function RegisterGeometryBufferRendererSceneComponent(geometryBufferRendererClass: typeof GeometryBufferRenderer): void {
    if (_Registered) {
        return;
    }
    _Registered = true;

    Object.defineProperty(Scene.prototype, "geometryBufferRenderer", {
        get: function (this: Scene) {
            return this._geometryBufferRenderer;
        },
        set: function (this: Scene, value: Nullable<GeometryBufferRenderer>) {
            if (value && value.isSupported) {
                this._geometryBufferRenderer = value;
            }
        },
        enumerable: true,
        configurable: true,
    });

    Scene.prototype.enableGeometryBufferRenderer = function (
        ratio: number | { width: number; height: number } = 1,
        depthFormat = Constants.TEXTUREFORMAT_DEPTH16,
        textureTypesAndFormats?: { [key: number]: IGeometryBufferTextureTypeAndFormat }
    ): Nullable<GeometryBufferRenderer> {
        if (this._geometryBufferRenderer) {
            return this._geometryBufferRenderer;
        }

        this._geometryBufferRenderer = new geometryBufferRendererClass(this, ratio, depthFormat, textureTypesAndFormats);
        if (!this._geometryBufferRenderer.isSupported) {
            this._geometryBufferRenderer = null;
        }

        return this._geometryBufferRenderer;
    };

    Scene.prototype.disableGeometryBufferRenderer = function (): void {
        if (!this._geometryBufferRenderer) {
            return;
        }

        this._geometryBufferRenderer.dispose();
        this._geometryBufferRenderer = null;
    };

    geometryBufferRendererClass._SceneComponentInitialization = (scene: Scene) => {
        // Register the G Buffer component to the scene.
        let component = scene._getComponent(SceneComponentConstants.NAME_GEOMETRYBUFFERRENDERER) as GeometryBufferRendererSceneComponent;
        if (!component) {
            component = new GeometryBufferRendererSceneComponent(scene);
            scene._addComponent(component);
        }
    };
}
